import React, { useCallback } from 'react'

import { Chip, Stack, Typography } from '@mui/material'
import type { SxProps, Theme } from '@mui/material'
import { alpha } from '@mui/material/styles'

import { categoryTintSx, filterChipSx } from '../helpers/filterChipStyles'

interface CountChipProps {
  label: string
  count?: number
  colorHex?: string
  selected: boolean
  // `value` + `onSelect` let callers pass a single stable handler instead of a
  // per-chip closure, so `React.memo` below can actually skip re-renders.
  value?: string
  onSelect?: (value: string) => void
  // Back-compat path for the "All …" chips (and any caller without a value).
  onClick?: () => void
}

/**
 * Shared filter chip for the Market Catalogue filter chrome. Used by both the
 * taxonomy rows (MarketAnalyticsFilterBar) and the event category/type rows
 * (CatalogueFilterBar) so every chip in the filter region looks identical.
 * `count` is optional — taxonomy options have no reliable counts and render
 * label-only. When a `colorHex` is supplied the whole chip is tinted with that
 * category color (border + text when idle, solid fill when selected) instead of
 * the brand purple. Memoized so a single chip toggling does not re-render the
 * whole filter region.
 */
const CountChipComponent: React.FC<CountChipProps> = ({ label, count, colorHex, selected, value, onSelect, onClick }) => {
  const handleClick = useCallback(() => {
    if (onSelect && value !== undefined) {
      onSelect(value)
    } else {
      onClick?.()
    }
  }, [onSelect, value, onClick])

  // Color-driven chips paint the full chip with the category color: a solid fill
  // when selected (contrast text auto-picked for legibility on light/dark hues)
  // and a tinted, colored-outline treatment when idle. Without a colorHex we fall
  // back to the default brand-purple filled / neutral-outline behavior.
  const colorSx: SxProps<Theme> | undefined = colorHex
    ? selected
      ? {
          backgroundColor: colorHex,
          borderColor: colorHex,
          // Always white on the solid category fill — the segment palette is
          // tuned so white reads on every hue (getContrastText would flip to
          // dark on the lighter ones, e.g. the olive Monetization color).
          color: (theme) => theme.palette.common.white,
          // Keyboard focus must keep the solid fill too — MUI's `&.Mui-focusVisible`
          // would otherwise swap it for a pale wash under the white label.
          '&:hover, &.Mui-focusVisible': { backgroundColor: colorHex, filter: 'brightness(0.93)' },
        }
      : {
          ...categoryTintSx(colorHex),
          '&:hover': { backgroundColor: alpha(colorHex, 0.2) },
        }
    : undefined

  return (
    <Chip
      size="small"
      clickable
      aria-pressed={selected}
      variant={selected ? 'filled' : 'outlined'}
      color={!colorHex && selected ? 'primary' : 'default'}
      onClick={handleClick}
      sx={[filterChipSx, ...(colorSx ? [colorSx] : [])]}
      label={
        <Stack direction="row" spacing={0.5} alignItems="center">
          <span>{label}</span>
          {count !== undefined ? (
            <Typography
              component="span"
              variant="caption"
              sx={{
                fontWeight: 'bold',
                color: colorHex ? 'inherit' : selected ? 'inherit' : 'text.secondary',
                opacity: selected ? 0.85 : 1,
                fontVariantNumeric: 'tabular-nums',
              }}
            >
              {count}
            </Typography>
          ) : null}
        </Stack>
      }
    />
  )
}

export const CountChip = React.memo(CountChipComponent)
